<template>
  <modal
    :name="name"
    height="auto"
    :width="width"
    :adaptive="true"
    :click-to-close="clickToClose"
    classes="confirm-dialog-modal"
  >
    <div class="confirm-dialog">
      <button
        type="button"
        class="confirm-dialog__close"
        aria-label="Close"
        @click="cancel"
      >
        <span class="mdi mdi-close"></span>
      </button>

      <div
        class="confirm-dialog__icon"
        :class="`confirm-dialog__icon--${variant}`"
      >
        <span class="mdi" :class="iconClass"></span>
      </div>

      <h3 v-if="title" class="confirm-dialog__title">{{ title }}</h3>
      <p class="confirm-dialog__message">{{ message }}</p>

      <div class="confirm-dialog__actions">
        <button
          type="button"
          class="confirm-dialog__btn confirm-dialog__btn--secondary"
          @click="cancel"
        >
          {{ cancelText }}
        </button>
        <button
          type="button"
          class="confirm-dialog__btn confirm-dialog__btn--primary"
          :disabled="loading"
          @click="$emit('confirm')"
        >
          {{ confirmText }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
export default {
  name: "ConfirmDialog",
  props: {
    name: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: "Confirm action"
    },
    message: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      default: "Yes, continue"
    },
    cancelText: {
      type: String,
      default: "Cancel"
    },
    width: {
      type: [String, Number],
      default: 420
    },
    variant: {
      type: String,
      default: "confirm",
      validator: value => ["confirm", "warning", "danger"].includes(value)
    },
    clickToClose: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    iconClass() {
      if (this.variant === "danger") {
        return "mdi-alert-circle-outline";
      }
      if (this.variant === "warning") {
        return "mdi-alert-outline";
      }
      return "mdi-help-circle-outline";
    }
  },
  methods: {
    cancel() {
      this.$modal.hide(this.name);
      this.$emit("cancel");
    }
  }
};
</script>

<style>
.confirm-dialog-modal {
  border-radius: var(--radius-lg) !important;
  overflow: hidden !important;
  box-shadow: var(--shadow-md), 0 20px 48px rgba(26, 19, 54, 0.14) !important;
  border: 1px solid var(--line) !important;
}
</style>

<style scoped>
.confirm-dialog {
  position: relative;
  padding: 28px 28px 24px;
  text-align: center;
  background: var(--surface);
}

.confirm-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--muted-soft);
  cursor: pointer;
  transition: background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}

.confirm-dialog__close:hover {
  background: var(--surface-2);
  color: var(--text);
}

.confirm-dialog__close .mdi {
  font-size: 1.25rem;
  line-height: 1;
}

.confirm-dialog__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-dialog__icon .mdi {
  font-size: 1.75rem;
  line-height: 1;
}

.confirm-dialog__icon--confirm {
  background: rgba(109, 84, 199, 0.12);
  color: var(--accent-600);
}

.confirm-dialog__icon--warning {
  background: rgba(245, 124, 0, 0.12);
  color: #ef6c00;
}

.confirm-dialog__icon--danger {
  background: rgba(198, 40, 40, 0.1);
  color: #c62828;
}

.confirm-dialog__title {
  margin: 0 0 8px;
  font-size: var(--fs-md, 1.125rem);
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink-900, var(--text));
}

.confirm-dialog__message {
  margin: 0 auto 24px;
  max-width: 320px;
  font-size: var(--fs-body);
  line-height: 1.55;
  color: var(--muted);
}

.confirm-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.confirm-dialog__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  height: 40px;
  padding: 0 18px;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--dur) var(--ease),
    border-color var(--dur) var(--ease), color var(--dur) var(--ease),
    box-shadow var(--dur) var(--ease);
}

.confirm-dialog__btn--secondary {
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--text);
}

.confirm-dialog__btn--secondary:hover {
  background: var(--surface-2);
  border-color: var(--line-strong, var(--line));
}

.confirm-dialog__btn--primary {
  border: 1px solid transparent;
  background: var(--grad-accent, var(--accent-600));
  color: #fff;
  box-shadow: 0 2px 8px rgba(109, 84, 199, 0.28);
}

.confirm-dialog__btn--primary:hover:not(:disabled) {
  background: var(--grad-accent-hover, var(--accent-600));
  box-shadow: 0 4px 12px rgba(109, 84, 199, 0.34);
}

.confirm-dialog__btn--primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
